import { useEffect, useSyncExternalStore } from "react";

/**
 * A shared timer that updates once per second.
 * All components using useCurrentTime share a single setInterval.
 * This prevents multiple intervals when multiple countdown components are mounted.
 */

let currentTime = Date.now();
const listeners = new Set<() => void>();
let intervalId: ReturnType<typeof setInterval> | null = null;

function subscribe(callback: () => void) {
  listeners.add(callback);

  // Start the interval when first subscriber joins
  if (listeners.size === 1 && !intervalId) {
    intervalId = setInterval(() => {
      currentTime = Date.now();
      listeners.forEach((listener) => listener());
    }, 1000);
  }

  return () => {
    listeners.delete(callback);

    // Stop the interval when last subscriber leaves
    if (listeners.size === 0 && intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
}

function getSnapshot() {
  return currentTime;
}

function getServerSnapshot() {
  return Date.now();
}

/**
 * Hook that returns the current timestamp, updating every second.
 * Uses a shared interval across all components to prevent multiple timers.
 *
 * @returns Current timestamp in milliseconds
 */
export function useCurrentTime(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
